// src/utils/api.js
import axios from 'axios';
import { APP_CONFIG } from '../appConfig';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: APP_CONFIG.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});


// ? Add request interceptor to inject token dynamically
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(APP_CONFIG.accessTokenStorageKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Generic API request methods
export const api = {
  get: async (endpoint, params = {}) => {
    try {
      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
  
  post: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
  
  put: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
  
  delete: async (endpoint) => {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }
};

// Error handling function
const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('API Error Response:', error.response.data);
    console.error('Status:', error.response.status);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('API Error Request:', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('API Error Message:', error.message);
  }
};

// Now let's create some specific financial calculation endpoints
export const financialService = {

  sendOTP: async (params) => {
    return api.post('/auth/send-otp', params);
  },
  verifyOTP: async (params) => {
    return api.post('/auth/verify-otp-and-create-profile', params);
  },
  
  submitRequest: async (params) => {
    return api.post('/submit',params);
  },

  submitGoals: async (phone_number,params) => {
    return api.post('/users/' + phone_number + '/goals',params);
  },
  deleteGoal: async (phone_number,params) => {
    return api.delete('/users/' + phone_number + '/goals/'+params);
  },
  submitCashFlow: async (phone_number, params) => {
    return api.post('/users/' + phone_number + '/cashflow',params);
  },

  submitInvestment: async (phone_number, params) => {
    return api.post('/users/' + phone_number + '/investment',params);
  },

  submitChildrenInfo: async (phone_number, params) => {
    return api.post('/users/' +phone_number + '/children',params);
  },

  loadUserData: async (phone_number) => {
    return api.get('/users/' + phone_number + '/complete-data');
  },

  emailRecord: async (phone_number) => {
    return api.post('/users/' + phone_number + '/emailRecord');
  },
  updateUserInfo: async (phone_number, params) => {
    return api.post('/users/' +phone_number + '/userInfoUpdate',params);
  },
  requestCallback: async (params) => {
    return api.post('/users/requestCallback',params);
  }
 };

// Wealth connect (MF Central now, Account Aggregator next)
export const wealthService = {
  getSummary: () => api.get('/wealth/summary'),
  getHoldings: (category) => api.get('/wealth/holdings/' + category),
  getInvestmentTotals: () => api.get('/wealth/investment-totals'),
  getSavedPan: () => api.get('/wealth/mfc/pan'),
  initiateMfc: (pan, mobile) => api.post('/wealth/mfc/initiate', { pan, mobile }),
  sendMfcEvent: (requestId, event) => api.post('/wealth/mfc/' + requestId + '/event', event),
  submitMfcQr: (requestId, qrBase64, casType) =>
    api.post('/wealth/mfc/' + requestId + '/qr', { qr_base64: qrBase64, cas_type: casType }),
  initiateAa: (connection, { mobile, pan } = {}) => api.post('/wealth/aa/initiate', { connection, mobile, pan }),
  cancelAa: (consentHandle) => api.post('/wealth/aa/' + consentHandle + '/cancel'),
  getAaStatus: (consentHandle) => api.get('/wealth/aa/' + consentHandle + '/status'),
  refreshAa: (connection) => api.post('/wealth/aa/refresh', { connection }),
};

// FastAPI error detail → readable message
export const apiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};
